import { Pipe, PipeTransform } from '@angular/core';
import { FinancialUtilsService } from '../../core/services';


/**
 * Money formatter that keeps sub-million figures readable instead of collapsing them.
 *
 * Use this wherever a column's real values can be small. `rmsFormatMinMillionMoney` renders
 * anything under $50,000 as the literal `$0M` and anything between $50,000 and $1M as a blunt
 * `$0.1M`, which is fine for market caps but wrong for a figure that is genuinely tens of
 * thousands of dollars — it displays a real number as zero.
 *
 * Behaviour:
 * - `null` passes through unchanged, so a caller's `—` fallback still applies.
 * - Zero renders as `0`, distinct from a missing value.
 * - Below $1M, renders in thousands: `31588.89` -> `31.6K`.
 * - A non-zero value too small to show at the requested precision renders as a floor marker
 *   (`<0.1K`, or `>-0.1K` when negative) rather than `0.0K`. Collapsing a real figure to
 *   something indistinguishable from zero is the exact failure this pipe exists to avoid, and at
 *   the default precision everything under $50 would otherwise do it.
 * - The unit is chosen *after* rounding, so the thousands branch can never emit `1000.0K`:
 *   `999999` rounds to `1000.0K`, which is really `1.0M`.
 * - At or above $1M, delegates to {@link FinancialUtilsService.convertMoneyMinMillion} so large
 *   values look identical to every other money column (`M`, `B`, `T` suffixes).
 *
 * Introduced for the RA-2842 margin columns, where 6-9% of real rows in the source sample fall
 * below the $50,000 floor. Not margin-specific — any small-money column should use it.
 */
@Pipe({
  name: 'rmsFormatThousandsMoney',
  standalone: false
})
export class RmsFormatThousandsMoneyPipe implements PipeTransform {

  private static readonly THOUSAND = 1000;

  transform(value: any, decimal: number = 1): string {
    if (value === null || value === undefined) {
      return value;
    }
    if (value === 0) {
      return '0';
    }

    // Round into thousands first and pick the unit from the rounded figure. Testing the raw value
    // against $1M instead would let 999,950-999,999 through to the thousands branch, where
    // toFixed(1) renders them as the self-contradictory '1000.0K'.
    const thousands = Number((value / RmsFormatThousandsMoneyPipe.THOUSAND).toFixed(decimal));
    if (Math.abs(thousands) >= RmsFormatThousandsMoneyPipe.THOUSAND) {
      return FinancialUtilsService.convertMoneyMinMillion(value, decimal);
    }

    // A non-zero value that rounds to zero at this precision. Note -0 === 0 in JavaScript, so the
    // sign has to come from the original value, not from `thousands`.
    if (thousands === 0) {
      const floor = Math.pow(10, -decimal).toFixed(decimal);
      return value > 0 ? `<${floor}K` : `>-${floor}K`;
    }

    return `${thousands.toFixed(decimal)}K`;
  }

}
